import React from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
  getUnlikelyAcceptanceCopy,
  parseSteamSignupReasons,
  STEAM_PRIVACY_INCOGNITO_STEPS,
  STEAM_PRIVACY_SETTINGS_URL,
} from "./steamSignupNotices";

const PAGE_SX = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at 50% 20%, rgba(232,163,61,0.16), transparent 34%), #080808",
  color: "#f7f4ec",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: 2,
  py: 8,
};

const CARD_SX = {
  width: "100%",
  maxWidth: 640,
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "18px",
  backgroundColor: "rgba(10, 12, 14, 0.92)",
  boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
  p: { xs: 3, md: 5 },
  textAlign: "center",
};

const EYEBROW_SX = {
  color: "#E8A33D",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  mb: 1.5,
};

const TITLE_SX = {
  fontSize: { xs: "2rem", md: "2.65rem" },
  lineHeight: 1.05,
  fontWeight: 900,
  letterSpacing: "-0.04em",
  mb: 2,
};

const BODY_SX = {
  color: "rgba(247,244,236,0.78)",
  fontSize: "1rem",
  lineHeight: 1.7,
  maxWidth: 520,
  mx: "auto",
  mb: 3,
  textAlign: "left",
};

const LIST_SX = {
  ...BODY_SX,
  pl: 2.5,
  mb: 3,
};

const BUTTON_SX = {
  borderRadius: "999px",
  px: 3,
  py: 1.15,
  fontWeight: 800,
  backgroundColor: "#E8A33D",
  color: "#101010",
  "&:hover": {
    backgroundColor: "#f0b657",
  },
};

const LINK_SX = {
  color: "#E8A33D",
  fontWeight: 700,
};

const PLAYSAFE_ERROR_CONTENT = {
  banned: {
    title: "You cannot play on Ouro right now",
    body:
      "Your PlaySafe account is not currently in good standing. Please resolve the issue with PlaySafe ID before attempting to play on Ouro.",
  },
  status_locked: {
    title: "You cannot play on Ouro right now",
    body:
      "Your PlaySafe account is locked. Please cooperate with PlaySafe ID to return your account to good standing before attempting to play on Ouro.",
  },
  status_temp: {
    title: "You cannot play on Ouro right now",
    body:
      "Your PlaySafe account has a temporary restriction. You can try again once the temporary ban expires.",
  },
  status_perm: {
    title: "You cannot play on Ouro right now",
    body:
      "Your PlaySafe account has a permanent offence. Ouro sign-up and play are not available for this PlaySafe ID.",
  },
  status_unverified: {
    title: "PlaySafe verification required",
    body:
      "Please complete KYC on PlaySafe ID, then return to Ouro and sign in again.",
  },
  steam_link_mismatch: {
    title: "Steam account not linked",
    body:
      "The Steam account you signed in with is not linked to your PlaySafe ID. Link this Steam account to your PlaySafe ID in the PlaySafe portal, then try again.",
  },
  steam_not_linked_to_playsafe: {
    title: "Steam account not linked",
    body:
      "This Steam account is not linked to any PlaySafe ID. Link it in the PlaySafe portal first, then connect it to Ouro.",
  },
  steam_link_no_session: {
    title: "Sign in to link Steam",
    body: "Please sign in to Ouro with PlaySafe ID first, then link your Steam account.",
  },
  steam_link_not_playsafe_user: {
    title: "Steam linking is for PlaySafe users",
    body: "Only accounts created via PlaySafe ID can link a Steam account through this flow.",
  },
  steam_link_not_configured: {
    title: "Steam linking unavailable",
    body: "Steam linking is not configured right now. Please try again later.",
  },
  steam_link_failed: {
    title: "Steam linking could not be completed",
    body: "We could not link your Steam account. Please return to your account page and try again.",
  },
  cs2_hours_not_public: {
    title: "Make your Steam profile public",
    body:
      "We cannot see your CS2 hours, so we cannot confirm this account. Update Steam privacy, then verify in an incognito window before connecting again.",
    steps: STEAM_PRIVACY_INCOGNITO_STEPS,
    privacySettingsLink: true,
    retryToConnectSteam: true,
  },
  kyc: {
    title: "PlaySafe verification required",
    body: "Please complete PlaySafe ID verification before attempting to play on Ouro.",
  },
  token: {
    title: "PlaySafe sign-in could not be completed",
    body: "We could not complete PlaySafe sign-in. Please return to the homepage and try again.",
  },
  userinfo: {
    title: "PlaySafe status could not be verified",
    body: "We could not verify your PlaySafe account status. Please return to the homepage and try again.",
  },
};

function getPlaySafeStatusContent({ errorCode, notice, reasons, steamLogin = false }) {
  if (notice === "steam_account_unlikely") {
    return {
      ...getUnlikelyAcceptanceCopy(parseSteamSignupReasons(reasons)),
      eyebrow: steamLogin ? "Steam sign-in" : "Application review",
      continueToApp: true,
    };
  }

  const normalizedErrorCode = String(errorCode || "").trim().toLowerCase();
  if (steamLogin) {
    if (normalizedErrorCode === "cs2_hours_not_public") {
      return {
        ...PLAYSAFE_ERROR_CONTENT.cs2_hours_not_public,
        eyebrow: "Steam sign-in",
        retryToConnectSteam: false,
        retrySteamSignIn: true,
      };
    }
    return {
      title: "Steam sign-in could not be completed",
      body: "Please return to the homepage and try Steam sign-in again.",
      eyebrow: "Steam sign-in",
    };
  }

  const content = PLAYSAFE_ERROR_CONTENT[normalizedErrorCode] || {
    title: "PlaySafe sign-in could not be completed",
    body: "Please return to the homepage and try again.",
  };

  return {
    ...content,
    eyebrow: "PlaySafe ID status",
  };
}

export default function PlaySafeStatusPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const steamError = params.get("steam_error");
  const steamNotice = params.get("steam_notice");
  const steamLogin = Boolean(steamError || steamNotice);
  const content = getPlaySafeStatusContent({
    errorCode: steamError || params.get("playsafe_error"),
    notice: steamNotice || params.get("playsafe_notice"),
    reasons: params.get("reasons"),
    steamLogin,
  });
  const steamLinkMode = params.get("steam_link") === "pending" ? "pending" : "session";

  return (
    <Box sx={PAGE_SX}>
      <Box sx={CARD_SX}>
        <Typography sx={EYEBROW_SX}>{content.eyebrow}</Typography>
        <Typography component="h1" sx={TITLE_SX}>
          {content.title}
        </Typography>
        <Typography sx={BODY_SX}>{content.body}</Typography>
        {content.privacySettingsLink ? (
          <Typography sx={BODY_SX}>
            Open{" "}
            <Box
              component="a"
              href={STEAM_PRIVACY_SETTINGS_URL}
              target="_blank"
              rel="noreferrer"
              sx={LINK_SX}
            >
              Steam privacy settings
            </Box>
            , then:
          </Typography>
        ) : null}
        {Array.isArray(content.steps) && content.steps.length > 0 ? (
          <Box component="ol" sx={LIST_SX}>
            {content.steps.map((step) => (
              <Typography key={step} component="li" sx={{ mb: 0.75 }}>
                {step}
              </Typography>
            ))}
          </Box>
        ) : null}
        {content.retryToConnectSteam ? (
          <Button
            component={Link}
            to={`/auth/playsafe/connect-steam?steam_link=${steamLinkMode}`}
            variant="contained"
            disableElevation
            sx={BUTTON_SX}
          >
            Try connecting Steam again
          </Button>
        ) : content.retrySteamSignIn ? (
          <Button component={Link} to="/" variant="contained" disableElevation sx={BUTTON_SX}>
            Try Steam sign-in again
          </Button>
        ) : content.continueToApp ? (
          <Button component={Link} to="/lobby" variant="contained" disableElevation sx={BUTTON_SX}>
            Continue
          </Button>
        ) : (
          <Button component={Link} to="/" variant="contained" disableElevation sx={BUTTON_SX}>
            Return to homepage
          </Button>
        )}
      </Box>
    </Box>
  );
}

export { getPlaySafeStatusContent };
